import { useCallback, useMemo, useState } from 'react'
import type { EntityWatcherDocument } from '@/lib/db/rxdb/schemas/entityWatcher'
import { createEntityHooks } from '@/lib/db/rxdb/hooks/core/createEntityHooks'
import useLoginStore from '@/store/LoginStore'

const watcherHooks = createEntityHooks<EntityWatcherDocument>('entitywatchers')

/**
 * Follow state for one entity (task or doc) for the current user, backed by
 * the synced `entitywatchers` collection.
 *
 * - Unfollow soft-deletes every active watcher row the user has on the entity
 *   (auto_creator / auto_assignee / manual alike).
 * - Follow re-activates a previously deleted row when one exists, otherwise
 *   inserts a `manual` watcher. Re-activating avoids the backend's
 *   "already watching" duplicate check and keeps one row per user per entity.
 *
 * `entityType` uses the backend value (`work_item`, `document`).
 */
export function useEntityWatch(entityType: string | null, entityId: string | null) {
  const userId = useLoginStore((s) => s.userId)
  const enabled = !!entityType && !!entityId && !!userId
  const filter = {
    entityType: entityType ?? undefined,
    entityId: entityId ?? undefined,
    userId,
  }
  const { data: active, isLoading } = watcherHooks.useEntities(
    { ...filter, isDeleted: false },
    { enabled },
  )
  const { data: removed } = watcherHooks.useEntities({ ...filter, isDeleted: true }, { enabled })
  const { create, update, delete: remove } = watcherHooks.useMutations()
  const [isPending, setIsPending] = useState(false)

  const isFollowing = active.length > 0

  const unfollow = useCallback(async () => {
    setIsPending(true)
    try {
      await Promise.all(active.map((w) => remove(w.id)))
    } finally {
      setIsPending(false)
    }
  }, [active, remove])

  const follow = useCallback(async () => {
    if (!entityType || !entityId || !userId) return
    setIsPending(true)
    try {
      const previous = [...removed].sort((a, b) => b.updatedOn - a.updatedOn)[0]
      if (previous) {
        await update(previous.id, {
          isDeleted: false,
          deletedOn: null,
          deletedBy: null,
          watchType: 'manual',
        })
      } else {
        await create({ entityType, entityId, userId, watchType: 'manual' })
      }
    } finally {
      setIsPending(false)
    }
  }, [create, entityId, entityType, removed, update, userId])

  return useMemo(
    () => ({ isFollowing, isLoading: enabled && isLoading, isPending, follow, unfollow }),
    [enabled, follow, isFollowing, isLoading, isPending, unfollow],
  )
}
